/**
 * ESimInventoryWidget Component
 *
 * Shows eSIM profile availability with low-stock warnings.
 * Displays available/in-use/downloaded counts.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard,
  AlertTriangle,
  CheckCircle2,
  Activity,
  Download,
  ExternalLink,
  Wifi,
} from 'lucide-react';
import { Card, Badge, Spinner } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/config/firebase';
import {
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';

interface ESimStats {
  total: number;
  available: number;
  inUse: number;
  downloaded: number;
  expired: number;
}

interface LowStockItem {
  networkName: string;
  available: number;
  total: number;
}

interface ESimInventoryWidgetProps {
  lowStockThreshold?: number; // Number of profiles below which to show warning
}

export const ESimInventoryWidget: React.FC<ESimInventoryWidgetProps> = ({
  lowStockThreshold = 3,
}) => {
  const navigate = useNavigate();
  const { activeOrganization } = useAuth();

  const [stats, setStats] = useState<ESimStats>({
    total: 0,
    available: 0,
    inUse: 0,
    downloaded: 0,
    expired: 0,
  });
  const [lowStockItems, setLowStockItems] = useState<LowStockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadESimData = async () => {
      if (!activeOrganization?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Load all eSIM profiles for this organization
        const profilesQuery = query(
          collection(db, 'esim_profiles'),
          where('organizationId', '==', activeOrganization.id)
        );
        const profilesSnapshot = await getDocs(profilesQuery);

        const profiles = profilesSnapshot.docs.map((doc) => doc.data());

        // Calculate stats
        const available = profiles.filter((p) => p.status === 'available').length;
        const inUse = profiles.filter((p) => p.status === 'in_use').length;
        const downloaded = profiles.filter((p) => p.status === 'downloaded').length;
        const expired = profiles.filter((p) => p.status === 'expired').length;

        setStats({
          total: profiles.length,
          available,
          inUse,
          downloaded,
          expired,
        });

        // Find low stock networks
        const networkCounts = new Map<string, { available: number; total: number; name: string }>();
        profiles.forEach((profile) => {
          const networkId = profile.networkId;
          const networkName = profile.networkName || 'Unknown Network';

          if (!networkCounts.has(networkId)) {
            networkCounts.set(networkId, { available: 0, total: 0, name: networkName });
          }

          const counts = networkCounts.get(networkId)!;
          counts.total++;
          if (profile.status === 'available') {
            counts.available++;
          }
        });

        // Filter for low stock
        const lowStock: LowStockItem[] = [];
        networkCounts.forEach((counts) => {
          if (counts.available <= lowStockThreshold && counts.available < counts.total) {
            lowStock.push({
              networkName: counts.name,
              available: counts.available,
              total: counts.total,
            });
          }
        });

        // Sort by available count (lowest first)
        lowStock.sort((a, b) => a.available - b.available);
        setLowStockItems(lowStock.slice(0, 3)); // Show top 3 low stock items

        setLoading(false);
      } catch (err) {
        console.error('Error loading eSIM data:', err);
        setError('Failed to load eSIM inventory');
        setLoading(false);
      }
    };

    loadESimData();

    // Refresh every 60 seconds
    const interval = setInterval(loadESimData, 60000);
    return () => clearInterval(interval);
  }, [activeOrganization?.id, lowStockThreshold]);

  if (loading) {
    return (
      <Card padding="lg">
        <div className="flex items-center justify-center py-8">
          <Spinner size="md" />
          <span className="ml-2 text-muted-foreground">Loading inventory...</span>
        </div>
      </Card>
    );
  }

  const hasLowStock = lowStockItems.length > 0;
  const hasProfiles = stats.total > 0;

  return (
    <Card padding="lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-muted-foreground" />
          <h3 className="text-h3 font-semibold text-foreground">
            eSIM Inventory
          </h3>
        </div>
        {hasLowStock && (
          <Badge className="bg-status-warning-bg text-status-warning">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Low Stock
          </Badge>
        )}
      </div>

      {!hasProfiles ? (
        <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
          <CreditCard className="w-12 h-12 mb-2 opacity-50" />
          <p className="text-sm">No eSIM profiles</p>
          <button
            onClick={() => navigate('/esim')}
            className="text-sm text-accent-blue hover:text-accent-blue/80 mt-2"
          >
            Add profiles
          </button>
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 rounded bg-status-success-bg border border-status-success/20">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4 text-status-success" />
                <span className="text-xs text-muted-foreground">Available</span>
              </div>
              <div className="text-2xl font-semibold text-status-success">
                {stats.available}
              </div>
            </div>

            <div className="p-3 rounded bg-accent-blue/10 border border-accent-blue/20">
              <div className="flex items-center gap-2 mb-1">
                <Activity className="w-4 h-4 text-accent-blue" />
                <span className="text-xs text-muted-foreground">In Use</span>
              </div>
              <div className="text-2xl font-semibold text-accent-blue">
                {stats.inUse}
              </div>
            </div>

            <div className="p-3 rounded bg-muted border border-border">
              <div className="flex items-center gap-2 mb-1">
                <Download className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Downloaded</span>
              </div>
              <div className="text-2xl font-semibold text-foreground">
                {stats.downloaded}
              </div>
            </div>

            <div className="p-3 rounded bg-muted border border-border">
              <div className="flex items-center gap-2 mb-1">
                <Wifi className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Total</span>
              </div>
              <div className="text-2xl font-semibold text-foreground">
                {stats.total}
              </div>
            </div>
          </div>

          {/* Low Stock Warnings */}
          {hasLowStock && (
            <div className="space-y-2 mb-4">
              <div className="text-xs font-medium text-status-warning uppercase tracking-wider flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Low Stock Networks
              </div>
              {lowStockItems.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 rounded bg-status-warning-bg/50 border border-status-warning/20"
                >
                  <span className="text-sm text-foreground truncate">
                    {item.networkName}
                  </span>
                  <span className="text-sm font-medium text-status-warning whitespace-nowrap ml-2">
                    {item.available} left
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* View all link */}
          <div className="pt-4 border-t border-border">
            <button
              onClick={() => navigate('/esim')}
              className="text-sm text-accent-blue hover:text-accent-blue/80 transition-colors flex items-center gap-1"
            >
              Manage eSIM profiles
              <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        </>
      )}
    </Card>
  );
};

export default ESimInventoryWidget;
